import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Platform,
  ActivityIndicator,
  Alert
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import theme from '../../../../config/theme';
import FormFieldError from '../../../../components/FormFieldError';
import FormattedInput from '../../../../components/FormattedInput';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { useWebAlert } from '../../../../components/WebAlert';
import { globalState } from '../../../../state/globalState';
import { supabase } from '../../../../services/supabase';
import { SettingsAddUsersLogger } from '../../../../utils/logger';
import { FunctionsHttpError } from '@supabase/supabase-js';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { getCompanyManagedUserTypeOptions } from '@/utils/userRoles';


// Logger para a página de adição de usuários
const logger = SettingsAddUsersLogger.Main;

const NewUserScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const { showAlert } = useWebAlert();

  // Estado do formulário
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    telefone: '',
    user_type: 'motorista'
  });

  // Estado de erro dos campos
  const [errors, setErrors] = useState<{
    email: string | null,
    password: string | null,
    name: string | null,
    telefone: string | null,
    user_type: string | null,
    general: string | null
  }>({
    email: null,
    password: null,
    name: null,
    telefone: null,
    user_type: null,
    general: null
  });

  // Estado de carregamento
  const [isLoading, setIsLoading] = useState(false);

  // Empresa atual (para associar o usuário)
  const companyData = globalState.company.data.get();

  // Estado para mostrar/esconder senha
  const [showPassword, setShowPassword] = useState(false);

  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string, onOk?: () => void) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK', onPress: onOk }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg, [{ text: 'OK', onPress: onOk }]);
    }
  };

  // Validação do formulário
  const validateForm = () => {
    const newErrors = {
      email: null as string | null,
      password: null as string | null,
      name: null as string | null,
      telefone: null as string | null,
      user_type: null as string | null,
      general: null as string | null
    };

    // Validar email
    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validar senha
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'A senha deve ter pelo menos 6 caracteres';
    }

    // Validar nome
    if (!formData.name) {
      newErrors.name = 'Nome é obrigatório';
    }

    // Validar tipo de usuário
    if (!formData.user_type) {
      newErrors.user_type = 'Tipo de usuário é obrigatório';
    }

    setErrors(newErrors);

    // Retornar se o formulário é válido
    return !Object.values(newErrors).some(error => error !== null);
  };

  // Criar usuário
  const handleCreateUser = async () => {
    try {
      logger.info('Iniciando handleCreateUser');

      // Validar formulário
      if (!validateForm()) {
        return;
      }

      setIsLoading(true);

      // Preparar dados
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        telefone: formData.telefone || '',
        empresa_id: companyData?.id,
        user_type: formData.user_type
      };

      logger.info('Criando usuário:', userData);

      try {
        // Chamar a Edge Function para criar usuário no Auth e no banco
        const { data, error } = await supabase.functions.invoke('admin-add-users', {
          body: userData
        });

        if (error) {
          logger.info('Erro recebido do invoke:', error);

          let customError: { message: string; code: string } | null = null;

          if (error instanceof FunctionsHttpError) {
            try {
              // ✨ aqui que o corpo JSON chega
              customError = await error.context.json();
              logger.info('Erro customizado extraído do context:', customError);
            } catch (e) {
              logger.info('Falha ao extrair JSON do error.context:', e);
            }
          }

          // Se reconheceu o email existente, lança mensagem amigável
          if (customError?.code === 'email_exists') {
            throw new Error('Este e-mail já está cadastrado. Por favor, escolha outro.');
          }

          // Caso contrário, use a message do JSON ou a message genérica
          throw new Error(
            customError?.message ||
            error.message ||
            'Erro ao criar usuário. Por favor, tente novamente mais tarde.'
          );
        }


        // Se chegou aqui, o usuário foi criado com sucesso
        // Agora vamos adicionar ao banco local também
        logger.info('Criando registro local');

        // Criar registro local (offline-first)
        await databaseService.createItem(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, {
          id: data.user.id,
          email: userData.email,
          name: userData.name,
          telefone: userData.telefone,
          empresa_id: userData.empresa_id,
          user_type: userData.user_type,
          created_at: data.user.created_at,
          updated_at: data.user.updated_at
        }, true); // true para skipPendingOperation

        logger.info('Registro local criado com sucesso');

        showMessage('Sucesso', 'Usuário criado com sucesso!', () => {
          // Voltar para a lista após criar usuário
          router.back();
        });
      } catch (error: any) {
        logger.info('Erro ao criar usuário no banco de dados:', error);
        throw new Error(error.message || 'Erro ao criar usuário no banco de dados');
      }
    } catch (error: any) {
      logger.info('Erro ao criar usuário:', error);
      const errorMessage = error.message || 'Ocorreu um erro ao criar o usuário';

      setErrors({
        ...errors,
        general: errorMessage
      });

      showMessage('Erro', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Atualizar campo do formulário
  const handleChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });

    // Limpar erro do campo quando o usuário digita
    if (errors[field as keyof typeof errors]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  // Função para voltar
  const handleGoBack = () => {
    router.back();
  };

  const userTypeOptions = getCompanyManagedUserTypeOptions();

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Novo Usuário</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.formContainer}>
          {errors.general && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{errors.general}</Text>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => handleChange('email', text)}
              placeholder="email@exemplo.com"
              placeholderTextColor={theme.colors.text.tertiary}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <FormFieldError message={errors.email} />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Senha</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <TextInput
                style={[styles.input, { flex: 1 }]}
                value={formData.password}
                onChangeText={(text) => handleChange('password', text)}
                placeholder="Mínimo 6 caracteres"
                placeholderTextColor={theme.colors.text.tertiary}
                secureTextEntry={!showPassword}
              />
              <TouchableOpacity
                onPress={() => setShowPassword((prev) => !prev)}
                style={{ marginLeft: 8, padding: 4 }}
                accessibilityLabel={showPassword ? 'Esconder senha' : 'Mostrar senha'}
              >
                <MaterialCommunityIcons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={22}
                  color={theme.colors.text.secondary}
                />
              </TouchableOpacity>
            </View>
            <FormFieldError message={errors.password} />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Nome</Text>
            <TextInput
              style={styles.input}
              value={formData.name}
              onChangeText={(text) => handleChange('name', text)}
              placeholder="Nome completo"
              placeholderTextColor={theme.colors.text.tertiary}
            />
            <FormFieldError message={errors.name} />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Telefone (opcional)</Text>
            <FormattedInput
              style={styles.input}
              value={formData.telefone}
              onChangeText={(text) => handleChange('telefone', text)}
              placeholder="(00) 00000-0000"
              placeholderTextColor={theme.colors.text.tertiary}
              type="phone"
            />
            <FormFieldError message={errors.telefone} />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Tipo de Usuário</Text>
            <View style={styles.radioGroup}>
              {userTypeOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.radioButton,
                    formData.user_type === option.value && styles.radioButtonSelected
                  ]}
                  onPress={() => handleChange('user_type', option.value)}
                >
                  <View style={styles.radioInner}>
                    {formData.user_type === option.value && (
                      <View style={styles.radioInnerSelected} />
                    )}
                  </View>
                  <Text style={styles.radioLabel}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <FormFieldError message={errors.user_type} />
            <View style={styles.userTypeInfoBox}>
              <Text style={styles.userTypeInfoTitle}>Permissões de cada perfil:</Text>
              <Text style={styles.userTypeInfoText}><Text style={styles.userTypeInfoStrong}>Motorista:</Text> pode ver e editar apenas os proprios checklists.</Text>
              <Text style={styles.userTypeInfoText}><Text style={styles.userTypeInfoStrong}>Administrador:</Text> pode ver e editar todos os checklists da propria empresa e gerenciar usuarios, empresa, assinatura e pagamentos.</Text>
            </View>
          </View>

          <TouchableOpacity
            style={[styles.submitButton, isLoading && styles.disabledButton]}
            onPress={handleCreateUser}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={theme.colors.text.inverse} />
            ) : (
              <Text style={styles.submitButtonText}>Criar Usuário</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingVertical: theme.spacing.lg,
  },
  formContainer: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    margin: theme.spacing.md,
    padding: theme.spacing.lg,
    ...theme.shadows.md,
  },
  errorContainer: {
    backgroundColor: `${theme.colors.util.error}20`,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  errorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.md,
  },
  inputGroup: {
    marginBottom: theme.spacing.lg,
  },
  label: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    backgroundColor: theme.colors.background.primary,
  },
  radioGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: theme.spacing.xs,
  },
  radioButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  },
  radioButtonSelected: {
    // Estilo para o botão selecionado
  },
  radioInner: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: theme.colors.primary.default,
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioInnerSelected: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.colors.primary.default,
  },
  radioLabel: {
    marginLeft: theme.spacing.xs,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  submitButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginTop: theme.spacing.md,
  },
  disabledButton: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  userTypeInfoBox: {
    backgroundColor: `${theme.colors.primary.default}10`,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.md,
    marginTop: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
  },
  userTypeInfoTitle: {
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.primary.default,
    marginBottom: 4,
  },
  userTypeInfoText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
    marginBottom: 2,
  },
  userTypeInfoStrong: {
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
  },
});

export default NewUserScreen;
